import { apiClient } from '../apiClient';

const getToken = () => localStorage.getItem('token');

const getTokenHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleMultipartFeedbackRequest = async (urlPath, method, payload, fallbackMessage) => {
  const baseUrl = String(apiClient.defaults?.baseURL || '').replace(/\/+$/, '');
  const response = await fetch(`${baseUrl}${urlPath}`, {
    method,
    headers: getTokenHeaders(),
    body: payload,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error || data?.message || fallbackMessage);
    error.response = { data, status: response.status };
    throw error;
  }

  return data;
};

export const getFeedbackBoardThreads = async (params = {}) => {
  try {
    const response = await apiClient.get('/pmiDevices/feedback-board', {
      params,
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching feedback board threads:', error);
    throw error;
  }
};

export const createFeedbackBoardThread = async (payload = {}) => {
  try {
    const isMultipart = typeof FormData !== 'undefined' && payload instanceof FormData;
    if (isMultipart) {
      return handleMultipartFeedbackRequest(
        '/pmiDevices/feedback-board',
        'POST',
        payload,
        'Failed to create feedback board thread'
      );
    }

    const response = await apiClient.post('/pmiDevices/feedback-board', payload, {
      headers: {
        ...getTokenHeaders(),
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating feedback board thread:', error);
    throw error;
  }
};

export const updateFeedbackBoardThread = async (id, payload = {}) => {
  try {
    const isMultipart = typeof FormData !== 'undefined' && payload instanceof FormData;
    if (isMultipart) {
      return handleMultipartFeedbackRequest(
        `/pmiDevices/feedback-board/${id}`,
        'PATCH',
        payload,
        'Failed to update feedback board thread'
      );
    }

    const response = await apiClient.patch(`/pmiDevices/feedback-board/${id}`, payload, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating feedback board thread:', error);
    throw error;
  }
};

export const addFeedbackBoardComment = async (id, payload = {}) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/comments`, payload, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error adding feedback board comment:', error);
    throw error;
  }
};

export const markFeedbackBoardThreadRead = async (id) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/read`, {}, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error marking feedback board thread as read:', error);
    throw error;
  }
};

export const markFeedbackBoardUpdateReviewed = async (id, eventKey) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/review-update`, { eventKey }, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error marking feedback update as reviewed:', error);
    throw error;
  }
};

export const approveFeedbackBoardRequest = async (id) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/approve-request`, {}, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error approving feedback board request:', error);
    throw error;
  }
};

export const logFeedbackBoardAiUsage = async (id, payload = {}) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/ai-usage`, payload, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error logging feedback board AI usage:', error);
    throw error;
  }
};

export const triggerFeedbackBoardRecurringReminder = async (id) => {
  try {
    const response = await apiClient.post(`/pmiDevices/feedback-board/${id}/trigger-recurring`, {}, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error triggering recurring feedback reminder:', error);
    throw error;
  }
};
